/**
 * Eligibility Notification View — the per-trial prescreening worklist, backed by
 * `GET /eligibility/options` and `POST /eligibility/screened-rows`.
 *
 * Shows every patient ever screened for the selected trial rather than one ingest
 * day's intake: the CRC works this list until each patient is dispositioned, so it
 * cannot reset when the next day's ingest runs.
 */

import { Archive } from "lucide-react";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clinicalTrialsService,
  type EligibilityScreenedRowsQuery,
} from "../api/clinicalTrialsService";
import { RisaCustomTable } from "../components/risa-custom-table";
import { LoaderMessage } from "../components/loaderMessage/loaderMessage";
import NoData from "../components/noData/noData";
import {
  buildEligibilityFilterSections,
  eligibilityOptionLabels,
} from "../data-model/eligibilityOptions";
import type { EligibilityTableRow } from "../data-model/eligibilityRows";
import { useEligibilityOptions } from "../hooks/useEligibilityOptions";
import { useEligibilityScreenedRows } from "../hooks/useEligibilityScreenedRows";
import {
  DEFAULT_TAB_VIEW_STATE,
  ELIGIBILITY_API_PAGE_SIZE,
  ELIGIBILITY_DISPLAY_PAGE_SIZE,
  type EligibilityTabViewState,
  type EligibilityWorklistReturnState,
  dateFilterIsoValue,
  firstFilterValue,
  getEligibilityApiPages,
  getEligibilityDisplayRows,
  sortEligibilityRowsByTier,
} from "../views/PatientEligibilityMappingView/eligibilityTableState";
import PatientEligibilityMappingHeader from "../views/PatientEligibilityMappingView/PatientEligibilityMappingHeader";
import PatientEligibilityMappingNavigation from "../views/PatientEligibilityMappingView/PatientEligibilityMappingNavigation";
import { AddMonitoringTrialModal } from "../views/PatientEligibilityMappingView/AddMonitoringTrialModal";
import { AnalyzePatientModal } from "../views/PatientEligibilityMappingView/AnalyzePatientModal";
import { EligibilityNotesNotificationModal } from "../views/PatientEligibilityMappingView/EligibilityNotesNotificationModal";
import { AdditionalTrialsCell } from "../views/PatientEligibilityMappingView/cells/AdditionalTrialsCell";
import { buildPatientStatusBadgeOptions } from "../utils/patientStatusBadges";
import { PatientStatusDropdown } from "../views/SinglePatientEligibilityDetailsView/header/PatientStatusHeaderDropdown";
import { TABLE_HEADERS } from "../views/PatientEligibilityMappingView/eligibilityMappingTableConfig";
import {
  ELIGIBILITY_ARCHIVE_TAB_VALUE,
  filterWorklistNavigationTabs,
  getEligibilityEmptyStateText,
} from "../enums/eligibilityWorklistTabs";
import { plainTextToRichText } from "../utils/richText";
import { useClinicalTrialsAdminAccess } from "../hooks/useClinicalTrialsAdminAccess";

/**
 * Patient Eligibility Mapping worklist backed by screened eligibility rows.
 *
 * Each tab preserves its own search, filters, sorting, page, and loading cache.
 * Selecting a tab sends its exact API value in the `tab` request filter. Tab
 * badges render only when the options API includes a count. Tenancy comes from
 * the JWT; these worklist calls never send a facility ID.
 *
 * Selecting a row opens that patient's eligibility details page, and Analyze
 * New Patient screens one MRN against the selected trial.
 *
 * @returns Eligibility mapping page
 */
export function PatientEligibilityMapping() {
  const location = useLocation();
  const navigate = useNavigate();
  const canAccessNotes = useClinicalTrialsAdminAccess();
  const eligibilityReturnState = (
    location.state as
      | { eligibilityReturnState?: EligibilityWorklistReturnState }
      | null
  )?.eligibilityReturnState;
  const [selectedTab, setSelectedTab] = useState(
    () => eligibilityReturnState?.selectedTab ?? "",
  );
  const [selectedTrialValue, setSelectedTrialValue] = useState(
    () => eligibilityReturnState?.selectedTrialValue ?? "",
  );
  const {
    options,
    loading: optionsLoading,
    error: optionsError,
    refresh: refreshOptions,
  } = useEligibilityOptions(selectedTrialValue);
  const [tabViewStates, setTabViewStates] = useState<
    Record<string, EligibilityTabViewState>
  >(() => eligibilityReturnState?.tabViewStates ?? {});
  const [isAnalyzePromptOpen, setIsAnalyzePromptOpen] = useState(false);
  const [isArchiveWorklist, setIsArchiveWorklist] = useState(
    () => eligibilityReturnState?.isArchiveWorklist ?? false,
  );
  const restoredEligibilityState = useRef(Boolean(eligibilityReturnState));
  const [monitoringTrialContext, setMonitoringTrialContext] = useState<{
    mrn: string;
    linkedTrialIds: string[];
  } | null>(null);
  const [isLinkingMonitoringTrial, setIsLinkingMonitoringTrial] = useState(false);
  const [notificationContext, setNotificationContext] = useState<{
    mrn: string;
    patientName: string;
  } | null>(null);
  const [locallyReadPatients, setLocallyReadPatients] = useState<Set<string>>(
    () => new Set(),
  );

  const navigationTabs = useMemo(
    () => (options ? filterWorklistNavigationTabs(options.tabs) : []),
    [options],
  );

  const activeWorklistTab = isArchiveWorklist
    ? ELIGIBILITY_ARCHIVE_TAB_VALUE
    : selectedTab;

  useEffect(() => {
    if (!options) return;
    const tabValues = navigationTabs.map((tab) => tab.value);
    setSelectedTab((current) =>
      current && tabValues.includes(current)
        ? current
        : (navigationTabs[0]?.value ?? ""),
    );
    const trialValues = options.trials.map((trial) => trial.value);
    setSelectedTrialValue((current) =>
      current && trialValues.includes(current)
        ? current
        : (options.trials[0]?.value ?? ""),
    );
  }, [navigationTabs, options]);

  useEffect(() => {
    if (restoredEligibilityState.current) {
      restoredEligibilityState.current = false;
      return;
    }
    setIsArchiveWorklist(false);
  }, [selectedTrialValue]);

  const currentViewState =
    tabViewStates[activeWorklistTab] ?? DEFAULT_TAB_VIEW_STATE;

  /**
   * Update only the currently selected tab's table controls.
   *
   * @param update - Partial tab state to merge
   * @returns Nothing
   */
  const updateCurrentTabState = useCallback(
    (update: Partial<EligibilityTabViewState>) => {
      if (!activeWorklistTab) return;
      setTabViewStates((current) => ({
        ...current,
        [activeWorklistTab]: {
          ...(current[activeWorklistTab] ?? DEFAULT_TAB_VIEW_STATE),
          ...update,
        },
      }));
    },
    [activeWorklistTab],
  );

  /**
   * Apply server-side worklist search from the shared table search bar.
   *
   * @param text - Trimmed query sent as `query` on screened-rows
   * @returns Nothing
   */
  const handleWorklistSearch = useCallback(
    (text: string) => {
      updateCurrentTabState({ searchText: text, page: 0 });
    },
    [updateCurrentTabState],
  );

  const filterSections = useMemo(
    () => (options ? buildEligibilityFilterSections(options) : []),
    [options],
  );
  const eligibilityTierLabels = useMemo(
    () => (options ? eligibilityOptionLabels(options.eligibility_tier) : {}),
    [options],
  );

  const screenedRowsQuery = useMemo<
    EligibilityScreenedRowsQuery[] | null
  >(() => {
    if (!selectedTrialValue || !activeWorklistTab) return null;
    const query: Omit<EligibilityScreenedRowsQuery, "page"> = {
      trialId: selectedTrialValue,
      tab: activeWorklistTab,
      query: currentViewState.searchText.trim() || undefined,
      patientStatus: firstFilterValue(
        currentViewState.appliedFilters,
        "patient_status",
      ),
      eligibilityStatus: firstFilterValue(
        currentViewState.appliedFilters,
        "eligibility_status",
      ),
      eligibilityTier: firstFilterValue(
        currentViewState.appliedFilters,
        "eligibility_tier",
      ),
      additionalStudy: firstFilterValue(
        currentViewState.appliedFilters,
        "additional_study",
      ),
      stage: firstFilterValue(currentViewState.appliedFilters, "stage"),
      diagnosisCode: firstFilterValue(
        currentViewState.appliedFilters,
        "diagnosis_code",
      ),
      nextMdVisitFrom: dateFilterIsoValue(
        currentViewState.appliedFilters,
        "next_md_visit",
        0,
      ),
      nextMdVisitTo: dateFilterIsoValue(
        currentViewState.appliedFilters,
        "next_md_visit",
        1,
      ),
      sortBy: currentViewState.sortBy,
      sortDir: currentViewState.sortDir,
      hitsPerPage: ELIGIBILITY_API_PAGE_SIZE,
    };
    return getEligibilityApiPages(currentViewState.page).map((page) => ({
      ...query,
      page,
    }));
  }, [
    currentViewState.appliedFilters,
    currentViewState.page,
    currentViewState.searchText,
    currentViewState.sortBy,
    currentViewState.sortDir,
    activeWorklistTab,
    selectedTrialValue,
  ]);

  const {
    rows,
    totalCount,
    loading: rowsLoading,
    error: rowsError,
    refresh: refreshRows,
    refreshAll: refreshAllRows,
  } = useEligibilityScreenedRows(screenedRowsQuery, { eligibilityTierLabels });
  const visibleRows = useMemo(() => {
    const orderedRows =
      currentViewState.sortBy === "eligibility_tier"
        ? sortEligibilityRowsByTier(rows, currentViewState.sortDir)
        : rows;
    return getEligibilityDisplayRows(orderedRows, currentViewState.page);
  }, [
    currentViewState.page,
    currentViewState.sortBy,
    currentViewState.sortDir,
    rows,
  ]);

  const patientStatusBadgeList = useMemo(
    () => buildPatientStatusBadgeOptions(options?.patient_status ?? []),
    [options?.patient_status],
  );

  /** Persist a confirmed patient workflow status change from the worklist. */
  const savePatientStatus = useCallback(
    async (
      mrn: string,
      nextStatus: string,
      comment: string,
    ): Promise<void> => {
      if (!selectedTrialValue) return;
      await clinicalTrialsService.patchEligibilityState(
        selectedTrialValue,
        mrn,
        {
          patient_status: nextStatus,
          comment: comment || null,
        },
      );
      toast.success("Patient status changed successfully.");

      if (comment) {
        try {
          await clinicalTrialsService.createNote(mrn, {
            title: `Patient status changed to ${nextStatus}`,
            body: plainTextToRichText(comment),
          });
          toast.success("Note added successfully.");
        } catch {
          toast.error(
            "Patient status was changed, but its note could not be added.",
          );
        }
      }

      refreshOptions();
      refreshAllRows();
    },
    [refreshAllRows, refreshOptions, selectedTrialValue],
  );

  /** Persist one user's bookmark state, then refresh rows so the icon reflects Firestore. */
  const togglePatientBookmark = useCallback(
    async (mrn: string, isBookmarked: boolean): Promise<void> => {
      if (!selectedTrialValue) return;
      try {
        if (isBookmarked) {
          await clinicalTrialsService.removeEligibilityBookmark(
            selectedTrialValue,
            mrn,
          );
        } else {
          await clinicalTrialsService.addEligibilityBookmark(
            selectedTrialValue,
            mrn,
          );
        }
        refreshAllRows();
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Could not update bookmark",
          "Bookmark update failed",
        );
      }
    },
    [refreshAllRows, selectedTrialValue],
  );

  /**
   * Attach interactive patient-status badge dropdowns to the visible worklist rows.
   *
   * @returns Table rows with CUSTOM_RENDER patient status cells
   */
  /**
   * Open the monitoring-trial linker for one worklist row.
   *
   * @param mrn - Patient medical record number
   * @param linkedTrialIds - Trials already linked from the source row
   * @returns Nothing
   */
  const openMonitoringTrialModal = useCallback(
    (mrn: string, linkedTrialIds: string[]) => {
      setMonitoringTrialContext({ mrn, linkedTrialIds });
    },
    [],
  );

  /**
   * Link the patient to another trial, then set CRC workflow fields on the target row.
   *
   * @param payload - Target trial id and disposition fields from step two
   * @returns Nothing once the link and patch succeed
   */
  const saveMonitoringTrial = useCallback(
    async (payload: {
      targetTrialId: string;
      eligibilityStatus: string;
      patientStatus: string;
    }): Promise<void> => {
      if (!selectedTrialValue || !monitoringTrialContext) return;
      setIsLinkingMonitoringTrial(true);
      try {
        await clinicalTrialsService.addMonitoringTrial(
          selectedTrialValue,
          monitoringTrialContext.mrn,
          payload.targetTrialId,
        );
        await clinicalTrialsService.patchEligibilityState(
          payload.targetTrialId,
          monitoringTrialContext.mrn,
          {
            patient_status: payload.patientStatus,
            eligibility_status: payload.eligibilityStatus,
          },
        );
        refreshAllRows();
      } finally {
        setIsLinkingMonitoringTrial(false);
      }
    },
    [monitoringTrialContext, refreshAllRows, selectedTrialValue],
  );

  const worklistTableData = useMemo(() => {
    return visibleRows.map((row) => {
      const readStateKey = `${selectedTrialValue}\u0000${row.id}`;
      const patientDetails = locallyReadPatients.has(readStateKey)
        ? {
            ...row.patientDetails,
            shouldShowLeftBorder: false,
            primaryTextColor: undefined,
          }
        : row.patientDetails;
      return {
        ...row,
        patientDetails,
        additionalTrials: {
          ...row.additionalTrials,
          render: () => (
            <AdditionalTrialsCell
              tags={row.additionalTrials.tags}
              onAddClick={() =>
                openMonitoringTrialModal(
                  row.id,
                  row.additionalTrials.linkedTrialIds,
                )
              }
            />
          ),
        },
        patientStatus: {
          sortValue: row.patientStatus.value,
          render: () => (
            <PatientStatusDropdown
              mrn={row.id}
              patientName={row.patientDetails.mainText}
              patientStatus={row.patientStatus.value}
              badgeList={patientStatusBadgeList}
              onSave={(nextStatus, comment) =>
                savePatientStatus(row.id, nextStatus, comment)
              }
            />
          ),
        },
        actionItems: {
          ...row.actionItems,
          onToggleBookmark: () =>
            void togglePatientBookmark(row.id, row.actionItems.isBookmarked),
          onOpenNotifications: () =>
            setNotificationContext({
              mrn: row.id,
              patientName: row.patientDetails.mainText,
            }),
        },
      };
    });
  }, [
    locallyReadPatients,
    openMonitoringTrialModal,
    patientStatusBadgeList,
    savePatientStatus,
    togglePatientBookmark,
    visibleRows,
    selectedTrialValue,
  ]);

  const openEligibilityNotification = useCallback(
    (notification: {
      note_id?: string | null;
      activity_id?: string | null;
      navigation_path?: string | null;
    }) => {
      if (!selectedTrialValue || !notificationContext) return;
      const { mrn } = notificationContext;
      setNotificationContext(null);
      if (notification.navigation_path) {
        navigate(notification.navigation_path);
        return;
      }
      if (!notification.note_id) {
        navigate(
          `/eligibility/${encodeURIComponent(selectedTrialValue)}/${encodeURIComponent(mrn)}`,
        );
        return;
      }
      const params = new URLSearchParams({ notes: "open", noteId: notification.note_id });
      if (notification.activity_id) params.set("activityId", notification.activity_id);
      navigate(
        `/eligibility/${encodeURIComponent(selectedTrialValue)}/${encodeURIComponent(mrn)}?${params.toString()}`,
      );
    },
    [navigate, notificationContext, selectedTrialValue],
  );

  const selectedTrialLabel =
    options?.trials.find((trial) => trial.value === selectedTrialValue)
      ?.label ?? selectedTrialValue;

  /**
   * Open the single-patient eligibility details page for the clicked row.
   *
   * @param row - Worklist table row whose `id` is the patient MRN
   * @returns Nothing
   */
  const openPatientDetails = useCallback(
    (row: EligibilityTableRow): void => {
      if (!selectedTrialValue || !row?.id) return;
      const trialId = selectedTrialValue;
      const readStateKey = `${trialId}\u0000${row.id}`;
      const isAlreadyRead =
        locallyReadPatients.has(readStateKey) ||
        !row.patientDetails.shouldShowLeftBorder;
      if (!isAlreadyRead) {
        setLocallyReadPatients((current) => {
          const next = new Set(current);
          next.add(readStateKey);
          return next;
        });
        void clinicalTrialsService.markEligibilityRead(trialId, row.id).catch(
          () => {
            setLocallyReadPatients((current) => {
              const next = new Set(current);
              next.delete(readStateKey);
              return next;
            });
          },
        );
      }
      navigate(
        `/eligibility/${encodeURIComponent(trialId)}/${encodeURIComponent(row.id)}`,
      );
    },
    [locallyReadPatients, navigate, selectedTrialValue],
  );

  /**
   * Queue the entered MRNs against the selected trial, then reload the worklist.
   *
   * @param mrns - Distinct patient medical record numbers from the analyze prompt
   * @returns The batch accounting, including anyone the server did not queue
   */
  const analyzePatient = useCallback(
    async (mrns: string[]) => {
      if (!selectedTrialValue) {
        throw new Error("Select a trial first");
      }
      const result = await clinicalTrialsService.analyzeEligibilityPatients(
        selectedTrialValue,
        mrns,
      );
      refreshRows();
      return result;
    },
    [refreshRows, selectedTrialValue],
  );

  if (optionsLoading && !options) {
    return (
      <div className="flex h-full min-h-0 flex-col p-3 bg-white">
        <LoaderMessage message="Fetching eligibility options ..." />
      </div>
    );
  }

  if (optionsError || !options) {
    return (
      <div className="flex h-full min-h-0 flex-col p-3 bg-white">
        <NoData text={optionsError || "Failed to load eligibility options"} />
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden flex-col gap-3 p-3 bg-white">
      <div className="patient-eligibility-mapping--header-section bg-white">
        <PatientEligibilityMappingHeader
          trials={options.trials}
          selectedTrialValue={selectedTrialValue}
          onTrialChange={setSelectedTrialValue}
          onOpenNotes={() =>
            navigate("/notes", {
              state: {
                eligibilityReturnState: {
                  selectedTab,
                  selectedTrialValue,
                  tabViewStates,
                  isArchiveWorklist,
                } satisfies EligibilityWorklistReturnState,
              },
            })
          }
          onAnalyzeNewPatient={() => setIsAnalyzePromptOpen(true)}
          showNotes={canAccessNotes}
          showAnalyzeNewPatient={!isArchiveWorklist}
        />
      </div>
      {!isArchiveWorklist && navigationTabs.length > 0 ? (
        <div className="patient-eligibility-mapping--navigation-section">
          <PatientEligibilityMappingNavigation
            tabs={navigationTabs}
            selectedTab={selectedTab}
            onTabChange={(tab) => setSelectedTab(tab)}
          />
        </div>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col overflow-auto">
        {rowsError && !rowsLoading ? (
          <NoData text={rowsError} />
        ) : (
          <RisaCustomTable
            tableName={`ct-worklist-${selectedTrialValue}-${activeWorklistTab}`}
            tableHeaders={TABLE_HEADERS}
            tableData={worklistTableData}
            count={visibleRows.length}
            totalCount={totalCount}
            itemsPerPage={ELIGIBILITY_DISPLAY_PAGE_SIZE}
            pagesPerView={5}
            hideSearchBar={false}
            isFetching={rowsLoading}
            noDataText={getEligibilityEmptyStateText(activeWorklistTab)}
            searchDefaultValue={currentViewState.searchText}
            showFilterButton
            showRefreshButton
            filterSectionsData={filterSections}
            isFilterApplied={Boolean(
              currentViewState.appliedFilters &&
              currentViewState.appliedFilters.filterCount > 0,
            )}
            searchingText={handleWorklistSearch}
            onFilterApplyClicked={(filters) =>
              updateCurrentTabState({ appliedFilters: filters, page: 0 })
            }
            onSortTableClicked={(fieldName, sortAscending) =>
              updateCurrentTabState({
                sortBy: fieldName,
                sortDir: sortAscending ? "asc" : "desc",
                page: 0,
              })
            }
            shouldDisableUiSorting
            serverSidePagination
            currentPage={currentViewState.page}
            onPageChange={(page) => updateCurrentTabState({ page })}
            onRefreshButton={refreshRows}
            placeholder="Search by patient name, MRN, diagnosis, or provider"
            showOtherButton
            otherButtonMeta={{
              text: "Archive",
              buttonType: isArchiveWorklist ? "primary" : "secondary",
              size: "medium",
              disabled: false,
              icon: <Archive className="h-4 w-4" aria-hidden />,
              onClick: () => setIsArchiveWorklist((current) => !current),
            }}
            rowClassName="group"
            onRowClick={openPatientDetails}
          />
        )}
      </div>
      {notificationContext && selectedTrialValue ? (
        <EligibilityNotesNotificationModal
          trialId={selectedTrialValue}
          mrn={notificationContext.mrn}
          patientName={notificationContext.patientName}
          onClose={() => setNotificationContext(null)}
          onOpenNotification={openEligibilityNotification}
          onUnreadCountChange={() => refreshRows()}
        />
      ) : null}
      <AnalyzePatientModal
        isOpen={isAnalyzePromptOpen}
        trialLabel={selectedTrialLabel}
        onClose={() => setIsAnalyzePromptOpen(false)}
        onAnalyze={analyzePatient}
      />
      <AddMonitoringTrialModal
        isOpen={Boolean(monitoringTrialContext)}
        context={monitoringTrialContext}
        sourceTrialId={selectedTrialValue}
        trials={options.trials}
        eligibilityTierOptions={options.eligibility_tier}
        eligibilityStatusOptions={options.eligibility_status}
        patientStatusOptions={options.patient_status}
        isSaving={isLinkingMonitoringTrial}
        onClose={() => setMonitoringTrialContext(null)}
        onSave={saveMonitoringTrial}
      />
    </div>
  );
}

export default PatientEligibilityMapping;
